<template>
  <div class="novel-data-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <div class="filter-item">
            <label class="filter-label">注册日期：</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 260px;"
            />
          </div>

          <!-- 快捷范围：一律「从昨天往前共 N 天」，不含今天（今天数据不完整） -->
          <div class="filter-item quick-ranges">
            <el-button
              v-for="q in quickRanges"
              :key="q.days"
              size="mini"
              :type="activeQuickRange === q.days ? 'primary' : ''"
              @click="applyQuickRange(q.days)"
            >{{ q.label }}</el-button>
          </div>

          <div class="filter-item">
            <label class="filter-label">N日内数据：</label>
            <el-select v-model="nDays" style="width: 130px;">
              <el-option v-for="o in nDaysOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">渠道：</label>
            <el-select v-model="channel" style="width: 120px;">
              <el-option v-for="o in channelOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">版本：</label>
            <el-select v-model="versionFilter" style="width: 170px;">
              <el-option-group label="及之后">
                <el-option v-for="v in versionOptionList" :key="'gte:' + v" :label="v + ' 及之后'" :value="'gte:' + v" />
              </el-option-group>
              <el-option-group label="仅此版本">
                <el-option v-for="v in versionOptionList" :key="'eq:' + v" :label="'仅 ' + v" :value="'eq:' + v" />
              </el-option-group>
            </el-select>
          </div>

          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          <el-button
            icon="el-icon-download"
            :loading="exporting"
            :disabled="!rows.length"
            @click="handleExport"
          >导出 Excel</el-button>
        </div>
        <div class="filter-tip">
          <strong>本页仅统计 App 版本{{ versionTipText }}的用户，其他版本用户不计入；1.0.16 起注册需绑定手机号：无手机号的 1.0.16 及之后版本用户不算注册成功，不计入本页任何用户指标。</strong><br />
          「初始小说」= 用户注册后首启直连进入的第一本小说。<strong>一本书一行，只列出该日期范围内确实有人以它为初始小说的书</strong>；所有比率的分母都是该行的「新用户数」。<br />
          日期范围 = 注册日期范围（圈定该期间新增的用户）；快捷按钮的「最近 N 天」= 从昨天往前共 N 天，不含今天。N日内 = 每个用户「注册当天(D0)起、到第 N 个自然日结束」窗口内的行为（0 日内 = 注册当天）。<br />
          <strong>发消息</strong> = 用户在 AI 剧情里亲自发出的消息（不含固定剧情台词），同一条消息在多份存档副本里只算一次；<strong>消费果肉</strong> = 节点解锁 + 道具购买 + 商城消费 + AI 对话按条扣费四处之和。<br />
          每组指标都分「<strong>任意小说</strong>」和「<strong>该小说内</strong>」两套：前者看这批用户在全平台的整体表现，后者只看他们在这本书里的表现。鼠标悬停单元格可看分子人数。
        </div>
      </el-card>
    </div>

    <!-- 数据表 -->
    <div class="result-section">
      <h3 class="section-title">小说数据</h3>
      <el-table
        v-if="rows.length"
        :data="rows"
        size="small"
        stripe
        border
        :header-cell-style="headerCellStyle"
        max-height="640"
      >
        <!-- 前两列冻结，横向滚动时始终可见「这是哪本书、多少人」 -->
        <el-table-column prop="title" label="小说名" min-width="220" fixed="left" show-overflow-tooltip />
        <el-table-column label="新用户数" width="100" align="right" fixed="left" sortable :sort-method="sortByNewUsers">
          <template slot-scope="scope">{{ formatCount(scope.row.new_users) }}</template>
        </el-table-column>

        <el-table-column
          v-for="group in columnGroups"
          :key="group.key"
          :label="group.label"
          align="center"
        >
          <el-table-column
            v-for="(t, i) in group.thresholds"
            :key="group.key + '-' + t"
            :label="group.unit(t)"
            width="88"
            align="center"
          >
            <template slot-scope="scope">
              <el-tooltip
                placement="top"
                :content="tooltipText(scope.row, group.key, i)"
                :open-delay="200"
              >
                <span :class="rateClass(scope.row[group.key][i], scope.row.new_users)">
                  {{ formatPercent(scope.row[group.key][i], scope.row.new_users) }}
                </span>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>

      <div v-else class="empty-tip">{{ searched ? '该筛选条件下没有初始小说数据' : '点击查询加载数据' }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { getAppVersions } from '@/api/platformNewData'
import { getNovelData, exportNovelData, NovelDataParams, NovelDataRow, NovelDataMeta } from '@/api/novelData'

// 版本筛选默认值：复合值 'gte:1.0.16'（及之后）/ 'eq:1.0.16'（仅此版本），请求时拆成 version + version_mode
const DEFAULT_VERSION = '1.0.16'
const DEFAULT_VERSION_FILTER = `gte:${DEFAULT_VERSION}`

// 默认日期范围天数：最近一周（从昨天往前 7 天）
const DEFAULT_RANGE_DAYS = 7

// 四组指标列。key 直接对应接口返回的字段名，thresholds 由接口 meta 下发（不写死在前端）。
interface ColumnGroup {
  key: 'msg_any' | 'msg_book' | 'pay_any' | 'pay_book'
  label: string
  thresholds: number[]
  unit: (t: number) => string
}

@Component
export default class NovelDataView extends Vue {
  // 仅用于吸收 router-view 透传的 prop，避免 attribute fallthrough，本页不使用
  @Prop() readonly game?: unknown
  @Prop() readonly games?: unknown

  loading = false
  exporting = false
  searched = false
  nDays: 0 | 1 | 3 | 7 | 14 | 30 = 0
  channel = 'all'
  versionFilter = DEFAULT_VERSION_FILTER
  appVersions: string[] = []
  dateRange: string[] = []

  rows: NovelDataRow[] = []
  meta: NovelDataMeta = { msg_thresholds: [], pay_thresholds: [] }

  // 查询序号：慢响应回来时若已有更新的查询，丢弃旧结果（防乱序渲染旧数据）
  private searchSeq = 0

  nDaysOptions = [
    { value: 0, label: '0日内（当天）' },
    { value: 1, label: '1日内' },
    { value: 3, label: '3日内' },
    { value: 7, label: '7日内' },
    { value: 14, label: '14日内' },
    { value: 30, label: '30日内' }
  ]

  channelOptions = [
    { value: 'toutiao', label: '头条' },
    { value: 'kuaishou', label: '快手' },
    { value: 'other', label: '其他' },
    { value: 'all', label: '所有' }
  ]

  // 快捷日期范围。days=1 就是「昨天」（从昨天往前共 1 天）
  quickRanges = [
    { days: 1, label: '昨天' },
    { days: 3, label: '最近3天' },
    { days: 7, label: '最近一周' },
    { days: 15, label: '最近15天' },
    { days: 30, label: '最近30天' }
  ]

  headerCellStyle = { background: '#f5f7fa', color: '#303133', fontWeight: '600' }

  get columnGroups(): ColumnGroup[] {
    return [
      { key: 'msg_any', label: '任意小说内发消息率', thresholds: this.meta.msg_thresholds, unit: t => `≥${t}条` },
      { key: 'msg_book', label: '该小说内发消息率', thresholds: this.meta.msg_thresholds, unit: t => `≥${t}条` },
      { key: 'pay_any', label: '任意小说内消费果肉率', thresholds: this.meta.pay_thresholds, unit: t => `≥${t}` },
      { key: 'pay_book', label: '该小说内消费果肉率', thresholds: this.meta.pay_thresholds, unit: t => `≥${t}` }
    ]
  }

  get versionOptionList(): string[] {
    return this.appVersions.includes(DEFAULT_VERSION) ? this.appVersions : [DEFAULT_VERSION, ...this.appVersions]
  }

  // 口径说明里的当前所选版本文案，如「 ≥ 1.0.16」/「 = 1.0.16」
  get versionTipText(): string {
    const [mode, ver] = this.versionFilter.split(':')
    return mode === 'eq' ? ` = ${ver}` : ` ≥ ${ver}`
  }

  // 当前日期范围正好等于某个快捷范围时高亮该按钮（手改日期后自然不高亮）
  get activeQuickRange(): number | null {
    const hit = this.quickRanges.find(q => {
      const [s, e] = this.rangeOf(q.days)
      return this.dateRange && this.dateRange[0] === s && this.dateRange[1] === e
    })
    return hit ? hit.days : null
  }

  created() {
    this.dateRange = this.rangeOf(DEFAULT_RANGE_DAYS)
    this.loadVersions()
    this.handleSearch()
  }

  async loadVersions() {
    try {
      this.appVersions = await getAppVersions()
    } catch (error) {
      // 拉不到就用 versionOptionList 的默认兜底项
      console.error('获取版本列表失败:', error)
    }
  }

  // 「最近 N 天」= 从昨天往前共 N 天（闭区间，不含今天——今天数据还没跑完）
  rangeOf(days: number): string[] {
    const today = new Date()
    const end = new Date(today)
    end.setDate(end.getDate() - 1)
    const start = new Date(today)
    start.setDate(start.getDate() - days)
    return [this.fmt(start), this.fmt(end)]
  }

  applyQuickRange(days: number) {
    this.dateRange = this.rangeOf(days)
    this.handleSearch()
  }

  // 当前筛选条件 → 请求参数。查询和导出共用，保证导出的就是屏幕上这份数据的口径
  currentParams(): NovelDataParams {
    const [mode, ver] = this.versionFilter.split(':')
    return {
      n_days: this.nDays,
      start_date: this.dateRange[0],
      end_date: this.dateRange[1],
      channel: this.channel as NovelDataParams['channel'],
      version: ver,
      version_mode: mode as NovelDataParams['version_mode']
    }
  }

  async handleSearch() {
    if (!this.dateRange || this.dateRange.length !== 2) return
    this.loading = true
    const seq = ++this.searchSeq
    try {
      const resp = await getNovelData(this.currentParams())
      if (seq !== this.searchSeq) return // 已有更新的查询，丢弃旧结果
      this.rows = resp.rows || []
      this.meta = resp.meta
      this.searched = true
    } catch (error) {
      console.error('获取小说数据失败:', error)
    } finally {
      if (seq === this.searchSeq) {
        this.loading = false
      }
    }
  }

  handleReset() {
    this.nDays = 0
    this.channel = 'all'
    this.versionFilter = DEFAULT_VERSION_FILTER
    this.dateRange = this.rangeOf(DEFAULT_RANGE_DAYS)
    this.handleSearch()
  }

  // 导出当前筛选条件下的表格。后端直接吐 xlsx，前端只负责把 blob 存成文件。
  async handleExport() {
    if (!this.rows.length) return
    this.exporting = true
    try {
      const res = await exportNovelData(this.currentParams())
      // 文件名以服务端返回的为准，解析不出来才用兜底名
      let filename = `小说数据-${this.dateRange[0]}~${this.dateRange[1]}.xlsx`
      const cd = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])
      const m = cd && /filename\*=UTF-8''([^;]+)/i.exec(cd)
      if (m && m[1]) filename = decodeURIComponent(m[1])

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // responseType 是 blob，出错时响应体也是 blob，读不出后端的 message，只能给通用提示
      console.error('导出小说数据失败:', error)
      this.$message.error('导出失败，请重试')
    } finally {
      this.exporting = false
    }
  }

  fmt(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  formatCount(value: number): string {
    if (value === undefined || value === null) return '-'
    return value.toLocaleString()
  }

  formatPercent(numerator: number, denominator: number): string {
    if (!denominator || numerator === undefined || numerator === null) return '-'
    return ((numerator / denominator) * 100).toFixed(1) + '%'
  }

  // 悬停显示分子/分母，便于核数（单元格本身只放百分比，24 列才排得下）
  tooltipText(row: NovelDataRow, key: ColumnGroup['key'], i: number): string {
    const n = row[key][i]
    if (n === undefined || n === null) return '-'
    return `${this.formatCount(n)} / ${this.formatCount(row.new_users)} 人`
  }

  // 率越高颜色越重，方便横向扫一眼找出表现好的书
  rateClass(numerator: number, denominator: number): string {
    if (!denominator) return ''
    const r = numerator / denominator
    if (r >= 0.5) return 'rate-high'
    if (r >= 0.2) return 'rate-mid'
    return 'rate-low'
  }

  sortByNewUsers(a: NovelDataRow, b: NovelDataRow): number {
    return a.new_users - b.new_users
  }
}
</script>

<style lang="scss" scoped>
.novel-data-wrapper {
  padding: 20px;
}

.filter-section {
  margin-bottom: 20px;

  .filter-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  }

  .filter-item {
    display: flex;
    align-items: center;
  }

  .filter-label {
    margin-right: 8px;
    font-size: 14px;
    color: #606266;
    white-space: nowrap;
  }

  .quick-ranges {
    gap: 4px;

    .el-button + .el-button {
      margin-left: 4px;
    }
  }

  .filter-tip {
    margin-top: 12px;
    font-size: 12px;
    line-height: 1.8;
    color: #909399;
  }
}

.result-section {
  .section-title {
    margin: 0 0 12px;
    padding-left: 10px;
    font-size: 16px;
    border-left: 4px solid #409eff;
  }

  .empty-tip {
    padding: 60px 0;
    text-align: center;
    color: #909399;
    background: #fff;
    border-radius: 4px;
  }
}

.rate-high {
  color: #67c23a;
  font-weight: 600;
}

.rate-mid {
  color: #e6a23c;
}

.rate-low {
  color: #909399;
}
</style>
